import React from "react";
import { Link } from "react-router-dom";
import { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ServicePageShell, {
  FaqList,
  RelatedServices,
  SectionWrap,
  buildServiceSchemas,
} from "../../components/ServicePageShell";
import ServiceInquiryForm from "../../components/ServiceInquiryForm";
import hero from "../../assets/img/Q.webp";
import webMobileHero from "../../assets/img/web-mobile-developmenthero.png";
import servicesBreakdownImg from "../../assets/img/services-breakdown.png";
import techStackImg from "../../assets/img/tech-stack.png";
import portfolioImg from "../../assets/img/portfolio.png";
import developmentProcessImg from "../../assets/img/development-process.png";
import webMobileFaqImg from "../../assets/img/web-mobile-developmentfaq.png";

gsap.registerPlugin(ScrollTrigger);

const pagePath = "/services/web-mobile-development";

const faqs = [
  {
    question: "Do you build both web apps and mobile apps?",
    answer:
      "Yes. We design and develop web platforms, iOS and Android mobile apps, progressive web apps, and the APIs that connect them.",
  },
  {
    question: "Which tech stack do you use?",
    answer:
      "We commonly work with React, Next.js, Node.js, Flutter, and React Native, selecting the stack that fits the product goals and launch timeline.",
  },
  {
    question: "Can you modernize an existing application?",
    answer:
      "Yes. We can rebuild, extend, or optimize an older product so performance, maintainability, and user experience improve together.",
  },
  {
    question: "How do you handle quality assurance and testing?",
    answer:
      "We include testing in the delivery process, covering functionality, responsiveness, critical user journeys, and release readiness.",
  },
  {
    question: "Will the product scale after launch?",
    answer:
      "We design the architecture, APIs, and release flow so the product can grow as usage, features, and team size increase.",
  },
];

const schemas = buildServiceSchemas({
  name: "Web & Mobile App Development",
  description:
    "QBITS9 is a custom web and mobile app development company India, building scalable products with React, Next.js, Node.js, Flutter, and React Native.",
  path: pagePath,
  serviceType: "custom web and mobile app development company India",
  faqs,
});

const heroStats = [
  {
    title: "Product-first builds",
    value: "Designed for usability, speed, and maintainability",
  },
  {
    title: "Cross-platform delivery",
    value: "Web, mobile, and API layers built together",
  },
  {
    title: "Launch ready",
    value: "Built to ship cleanly and scale after release",
  },
];

const services = [
  {
    badge: "Web",
    title: "Web Apps",
    description:
      "High-performance web applications for customer portals, dashboards, internal tools, marketplaces, and admin systems.",
  },
  {
    badge: "iOS",
    title: "Mobile Apps",
    description:
      "Modern iOS and Android experiences built for usability, retention, and product growth.",
  },
  {
    badge: "PWA",
    title: "Progressive Web Apps",
    description:
      "App-like browser experiences that feel fast, installable, and easy to deploy across devices.",
  },
  {
    badge: "API",
    title: "API Development",
    description:
      "Robust APIs that connect applications, automate data flow, and make future integrations easier.",
  },
];

const techStack = [
  {
    badge: "R",
    title: "React",
    description:
      "Component-driven interfaces with a strong focus on performance and reusability.",
  },
  {
    badge: "N",
    title: "Next.js",
    description:
      "Fast, SEO-friendly web experiences that are ready for production scale.",
  },
  {
    badge: "N",
    title: "Node.js",
    description:
      "Backend services and APIs built for flexible integrations and predictable scale.",
  },
  {
    badge: "F",
    title: "Flutter",
    description:
      "Cross-platform mobile apps with a unified codebase and a polished interface.",
  },
  {
    badge: "RN",
    title: "React Native",
    description:
      "Native-style mobile applications with efficient delivery cycles.",
  },
];

const projectCards = [
  {
    badge: "01",
    title: "Operations dashboard for internal teams",
    description:
      "A custom admin and reporting interface that helps founders and managers track orders, approvals, team activity, and business KPIs in one place.",
    metric: "Use case: internal visibility and decision support",
  },
  {
    badge: "02",
    title: "Mobile commerce experience for growing brands",
    description:
      "A customer-facing mobile app focused on product discovery, smooth checkout journeys, repeat purchase flows, and loyalty-friendly UX.",
    metric: "Use case: retention and mobile revenue growth",
  },
  {
    badge: "03",
    title: "API and integration control center",
    description:
      "A backend operations tool that keeps integration health, scheduled jobs, audit logs, and system alerts organized in one control layer.",
    metric: "Use case: cleaner integrations and maintenance",
  },
];

const developmentSteps = [
  {
    label: "Discovery",
    title: "Define the product scope",
    description:
      "We clarify the audience, business goals, core features, and technical constraints before the build starts.",
  },
  {
    label: "Design",
    title: "Shape the user experience",
    description:
      "We plan the flows, screens, and interaction patterns that will matter most to users and stakeholders.",
  },
  {
    label: "Development",
    title: "Build the app or platform",
    description:
      "We implement the front end, back end, and integrations in a clean and testable structure.",
  },
  {
    label: "Testing",
    title: "Check performance and quality",
    description:
      "We verify features, usability, responsive behavior, and release stability before launch.",
  },
  {
    label: "Launch",
    title: "Deploy and support",
    description:
      "We release the product and stay available for iteration, fixes, and scale-up planning.",
  },
];

const relatedLinks = [
  {
    to: "/",
    kicker: "Home",
    title: "Back to QBITS9",
    description: "Return to the homepage and explore the company story, clients, and services overview.",
    cta: "Open home",
  },
  {
    to: "/services/ai-automation",
    kicker: "Automation",
    title: "AI automation services",
    description: "Reduce repetitive work and connect your stack with intelligent workflows.",
    cta: "View service",
  },
  {
    to: "/services/saas-erp-development",
    kicker: "Platform work",
    title: "SaaS & ERP development",
    description: "Create secure multi-tenant platforms and internal systems around business operations.",
    cta: "View service",
  },
  {
    to: "/services/desktop-software",
    kicker: "Desktop tools",
    title: "Desktop software",
    description: "Build desktop apps, offline utilities, and connected internal software.",
    cta: "View service",
  },
  {
    to: "/contact",
    kicker: "CTA",
    title: "Start your project",
    description: "Tell us what you want to build and we will help shape the roadmap.",
    cta: "Contact us",
  },
];

const WebMobileDevelopment = () => {
  const textSectionRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      const setupSplitAnimation = ({ pin, start, end }) => {
        const textElement = textRef.current;
        const sectionElement = textSectionRef.current;
        if (!textElement || !sectionElement) return undefined;

        const textContent = textElement.textContent;
        textElement.innerHTML = "";

        textContent.split("").forEach((char) => {
          const span = document.createElement("span");
          span.textContent = char;
          span.style.color = "#8C8C8C";
          textElement.appendChild(span);
        });

        const chars = textElement.querySelectorAll("span");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionElement,
            start,
            end,
            pin,
            scrub: 2,
            invalidateOnRefresh: true,
          },
        });

        tl.to(chars, {
          color: "#000000",
          stagger: 0.1,
          ease: "none",
        });

        return () => {
          tl.kill();
          textElement.textContent = textContent;
        };
      };

      mm.add("(min-width: 768px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=150%",
        })
      );

      mm.add("(max-width: 767px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=120%",
        })
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <ServicePageShell
      title="Web & Mobile App Development | QBITS9"
      description="QBITS9 is a custom web and mobile app development company India, building React, Next.js, Node.js, Flutter, and React Native products that scale."
      canonicalPath={pagePath}
      keywords="custom web and mobile app development company India, web app development, mobile app development, Flutter development, React Native development, Next.js development"
      schemas={schemas}
    >
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 top-[4%] md:w-[350px] w-[280px]">
          <img
            src={hero}
            alt="Web and mobile development services illustration"
            className="w-full h-auto aspect-auto"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="800"
            height="800"
          />
        </div>
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 lg:px-16">
          <nav className="flex flex-wrap items-center gap-2 lg:text-base text-sm font-[500] text-blue font-poppins lg:w-[65%] w-full px-5 lg:px-0">
            <Link to="/" className="hover:text-[#FF6B00]">
              Home
            </Link>
            <span>/</span>
            <span>Services</span>
            <span>/</span>
            <span className="text-slate-900">Web & Mobile Development</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-5">
                <span className="font-peachi text-[16px] md:text-[18px] text-black font-[500] text-black mb-3">
                  Product builds for startups, brands, and internal teams
                </span>
                <h1 className="text-black xl:text-[58px] lg:text-[50px] md:text-[42px] text-[28px] font-bold leading-[1.3]">
                  Custom web and mobile app development company India for products that scale.
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-slate-600 font-poppins">
                  We design and build fast web apps, mobile apps, progressive web apps, and API-driven systems using React, Next.js, Node.js, Flutter, and React Native.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group cursor-pointer"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">Start Your Project</span>
                  <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[25rem] group-hover:w-[25rem]" />
                </Link>
                <Link
                  to="/services/saas-erp-development"
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">Explore SaaS & ERP Development</span>
                  <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[25rem] group-hover:h-[25rem] -translate-x-1/2 -translate-y-1/2" />
                </Link>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {heroStats.map((stat) => (
                  <article
                    key={stat.title}
                    className="h-full bg-white rounded-[22px] py-[18px] px-[18px] flex flex-col gap-2 shadow-[0_12px_30px_0_#0000000F] hover:shadow-[0_12px_30px_0_#00000030] transition duration-500 ease-in-out cursor-pointer"
                  >
                    <p className="text-[18px] font-bold text-primary">
                      {stat.title}
                    </p>
                    <p className="text-[15px] font-light text-lightBlack line-clamp-3">
                      {stat.value}
                    </p>
                  </article>
                ))}
              </div>
            </div>

            <div className="lg:pl-4">
              <div className="">
                <img
                  className="rounded-[40px]"
                  src={webMobileHero}
                  alt="Product team reviewing web and mobile app screens during a planning session."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TEXT SPLIT PIN SECTION ---------- */}
      <section
        ref={textSectionRef}
        className="text_animation min-h-screen lg:mt-[-150px] mt-0 flex items-center justify-center px-5 lg:py-0 py-[80px] w-full overflow-hidden"
      >
        <div
          aria-hidden="true"
          ref={textRef}
          className="font-peachi text-[#8C8C8C] text-[22px] md:text-[30px] lg:text-[38px] leading-[1.7] max-w-6xl text-center"
        >
          QBITS9 builds custom web and mobile products for businesses in India that combine strong user experience, clean engineering, and scalable architecture. From responsive web apps and mobile experiences to API-driven systems, we help teams launch faster and grow with confidence.
        </div>
        <p className="sr-only">
          QBITS9 builds custom web and mobile products for businesses in India that combine strong user experience, clean engineering, and scalable architecture.
        </p>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Services breakdown </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  The delivery model stays focused on product outcomes
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  We choose a delivery path that fits your launch goals, user needs, and long-term support plans.
                </p>
              </div>
                   <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-2">
           {services.map((item) => (
                  <article
                    key={item.title}
                    className="group flex h-full flex-col justify-between rounded-[30px] border border-slate-200 bg-white p-8 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-6">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
                        {item.badge}
                      </span>
                      <h3 className="text-xl font-semibold text-slate-900 font-unbounded leading-tight">
                        <span className="text-primary">{item.title.split(" ")[0]}</span>
                        {item.title.split(" ").length > 1 ? ` ${item.title.split(" ").slice(1).join(" ")}` : ""}
                      </h3>
                      <p className="text-sm leading-7 text-slate-600 font-poppins">
                        {item.description}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={servicesBreakdownImg}
                alt="Web and mobile development services breakdown illustration"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 lg:items-start pt-10">
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="space-y-4">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Tech stack </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  Modern tools for fast, maintainable delivery
                </h2>
                <p className="text-base leading-8 text-slate-600 font-poppins">
                  We work with proven frameworks and runtimes that support strong user experience, clean code, and future growth.
                </p>
              </div>
              <div className="mt-4">
                <img
                  src={techStackImg}
                  alt="Tech stack illustration"
                  className="w-full h-auto rounded-[24px] lg:rounded-[32px] shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {techStack.map((item, index) => (
                <article
                  key={item.title}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className="rounded-[24px] lg:rounded-[32px] border border-slate-100 bg-white p-6 lg:p-8 flex flex-col justify-between shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_50px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="space-y-6">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
                      {item.badge}
                    </span>
                    <h3 className="text-xl font-bold text-slate-900 font-unbounded leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm leading-7 text-slate-600 font-poppins">
                      {item.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Portfolio </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  Typical product builds we help teams plan and deliver
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  These examples show the kinds of web, mobile, and API-led systems we commonly help businesses design, scope, and launch.
                </p>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  Many of these builds become more valuable when paired with <Link to="/services/ai-automation" className="text-primary underline hover:text-secondary">AI automation services</Link> or stronger <Link to="/services/saas-erp-development" className="text-primary underline hover:text-secondary">SaaS and ERP systems</Link> behind the scenes.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-1 xl:grid-cols-3">
                {projectCards.map((item) => (
                  <article
                    key={item.title}
                    className="group flex h-full flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-4">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
                        {item.badge}
                      </span>
                      <p className="text-[13px] font-[400] text-slate-900">
                        {item.metric}
                      </p>
                      <h3 className="text-[18px] font-semibold text-slate-900 font-unbounded leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-7 text-slate-600 font-poppins">
                        {item.description}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={portfolioImg}
                alt="Portfolio showcase for web and mobile product builds"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 lg:items-start pt-10">
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="space-y-4">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Development process </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  A five-step process that keeps product delivery organised
                </h2>
                <p className="text-base leading-8 text-slate-600 font-poppins">
                  We use a clear flow so business goals, design decisions, and technical execution stay aligned throughout the build.
                </p>
              </div>
              <div className="mt-4">
                <img
                  src={developmentProcessImg}
                  alt="Development process illustration"
                  className="w-full h-auto rounded-[24px] lg:rounded-[32px] shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {developmentSteps.map((step, index) => (
                <article
                  key={step.title}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className="rounded-[24px] lg:rounded-[32px] border border-slate-100 bg-white p-6 lg:p-8 flex flex-col justify-between shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_50px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="space-y-6">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-sm font-semibold text-[#FF6B00]">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      {step.label || "Step"}
                    </p>
                    <h3 className="text-xl font-bold text-slate-900 font-unbounded leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm leading-7 text-slate-600 font-poppins">
                      {step.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionWrap
        eyebrow="FAQ"
        title="Questions we hear before starting a product build"
        description="These answers help teams understand the process, stack choices, and scaling approach before the project begins."
        image={webMobileFaqImg}
        imageAlt="Frequently asked questions about web and mobile app development"
        imagePosition="right"
      >
        <FaqList faqs={faqs} />
      </SectionWrap>

      <SectionWrap
        eyebrow="CTA"
        title="Start your product build with a clear roadmap"
        description="If you have a web app, mobile app, or API idea, we can help define the scope, choose the stack, and plan a practical delivery path."
        suggestion={{
          title: "Suggested CTA image",
          text: "Use a founder and product team reviewing wireframes, backlog cards, and a launch checklist on a bright white table with orange accents."
        }}
        rightContent={
          <div data-aos="fade-left">
            <ServiceInquiryForm
              title="Tell us about your product idea"
              description="Share the platform type, audience, and features you need. We will respond with a practical build approach."
              submitLabel="Start Your Project"
              serviceName="Web & Mobile App Development"
              defaultSource="Web & Mobile Development page"
              sourceOptions={[
                "Web & Mobile Development page",
                "Referral",
                "Search",
                "Social media",
                "Other",
              ]}
              successTitle="Your product enquiry is in"
              successDescription="Thanks for sharing the details. We will review your product goals and get back to you soon."
            />
          </div>
        }
      >
        <div className="space-y-6" id="service-inquiry">
          <p className="text-sm leading-7 text-slate-600 font-poppins">
            If your product also needs smarter workflows, see how we support <Link to="/blog/how-to-build-a-smarter-operations-stack-for-a-growing-brand" className="text-primary underline hover:text-secondary">a smarter operations stack</Link> or connect the product with <Link to="/services/ai-automation" className="text-primary underline hover:text-secondary">AI automation services</Link>.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              "Plan the product before development starts",
              "Choose the right stack for the roadmap",
              "Reduce rework with a tight discovery phase",
              "Build for launch and future scale",
            ].map((item, index) => (
              <div
                key={item}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="rounded-[10px] border border-slate-200 bg-[#FFF9F4] px-4 py-3 text-[15px] font-light text-lightBlack line-clamp-3 transition-all duration-300 hover:border-primary hover:shadow-md"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </SectionWrap>

      <RelatedServices links={relatedLinks} variant="service" />
    </ServicePageShell>
  );
};

export default WebMobileDevelopment;
